import { type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Spinner } from "../../feedback/Spinner";

export type ChatBubbleProps = {
  sender: "user" | "assistant" | "system";
  children?: ReactNode;
  eyebrow?: ReactNode;
  title?: ReactNode;
  timestamp?: ReactNode;
  status?: ReactNode;
  loading?: boolean;
  errorAction?: ReactNode;
  className?: string;
};

export function ChatBubble({
  sender,
  children,
  eyebrow,
  title,
  timestamp,
  status,
  loading,
  errorAction,
  className
}: ChatBubbleProps) {
  const isUser = sender === "user";
  const isAssistant = sender === "assistant";

  const content = loading ? (
    <Spinner size="sm" />
  ) : isAssistant ? (
    <div className="bfrs-flex bfrs-w-full bfrs-flex-col bfrs-items-start bfrs-gap-2 bfrs-[word-break:break-word]">
      {eyebrow && (
        <div className="bfrs-flex bfrs-w-full bfrs-flex-col bfrs-justify-center bfrs-text-[10px] bfrs-font-bold bfrs-uppercase bfrs-leading-normal bfrs-tracking-[1.6px] bfrs-text-[var(--bfrs-dcl-color-chat-assistant-eyebrow)]">
          {eyebrow}
        </div>
      )}
      {(title || children) && (
        <div className="bfrs-flex bfrs-w-full bfrs-flex-col bfrs-items-start bfrs-gap-2">
          {title && (
            <div className="bfrs-w-full bfrs-text-[20px] bfrs-font-semibold bfrs-leading-normal bfrs-text-[var(--bfrs-dcl-color-chat-assistant-title)]">
              {title}
            </div>
          )}
          {children && (
            <div className="bfrs-max-w-[369px] bfrs-text-[14px] bfrs-font-normal bfrs-leading-[17px] bfrs-text-[var(--bfrs-dcl-color-chat-assistant-body)]">
              {children}
            </div>
          )}
        </div>
      )}
    </div>
  ) : (
    children
  );

  return (
    <div className={cn("bfrs-flex bfrs-w-full", isUser ? "bfrs-justify-end" : "bfrs-justify-start", className)}>
      <div
        className={cn(
          "bfrs-max-w-[min(84%,40rem)]",
          !isAssistant && "bfrs-rounded-2xl bfrs-px-4 bfrs-py-3 bfrs-text-sm bfrs-leading-6 bfrs-shadow-low",
          isUser && "bfrs-rounded-br-sm bfrs-bg-primary-soft bfrs-text-text-primary",
          isAssistant && "bfrs-max-w-[min(100%,23.125rem)] bfrs-text-left",
          sender === "system" && "bfrs-bg-surface-muted bfrs-text-text-muted"
        )}
      >
        {content}
        {(timestamp || status || errorAction) && (
          <div className="bfrs-mt-2 bfrs-flex bfrs-items-center bfrs-justify-between bfrs-gap-3 bfrs-text-xs bfrs-text-text-muted">
            <span>{timestamp}</span>
            <span>{errorAction ?? status}</span>
          </div>
        )}
      </div>
    </div>
  );
}
